<template>
  <div
    v-show="shouldShow"
    ref="dynamicPaymentButtonsTarget"
    class="dynamic-checkout-buttons"
    :class="{ 'dynamic-checkout-buttons--outline': outlineWallet }"
  ></div>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import { getFeatureToggles } from "@/helpers/featureToggles.js";

const OUTLINE_WALLETS = new Set(["apple_pay", "google_pay"]);

// Mirrors the inline style on #dynamic-payment-buttons in
// shopify/sections/main-product.liquid. Restored when this component unmounts
// so the block is never left visible (e.g. the CTA row swaps to the stock
// notification form after a sold-out size is selected).
const HIDDEN_STYLE =
  "position:absolute;top:0;left:-200vw;width:100%;max-width:600px;opacity:0;pointer-events:none;";

export default {
  name: "DynamicCheckoutButtonsV2",
  data() {
    return {
      outlineWallet: false,
    };
  },
  computed: {
    ...mapState("product", ["selectedVariant", "product"]),
    ...mapGetters("product", [
      "variants",
      "availableForPreOrder",
      "availableForPrebuy",
      "isSelectedVariantPreorder",
      "isPreLaunch",
    ]),
    expressCheckoutVersion() {
      const { expressCheckoutVersion = 0 } = getFeatureToggles();
      return expressCheckoutVersion;
    },
    isGiftCard() {
      const type = this.product?.productType?.toLowerCase?.() || "";
      const title = this.product?.title?.toLowerCase?.() || "";
      const handle = this.product?.handle?.toLowerCase?.() || "";

      return (
        type.includes("gift") ||
        title.includes("gift card") ||
        handle.includes("gift-card")
      );
    },
    variantIsPurchasable() {
      return this.isPurchasable(this.selectedVariant);
    },
    allVariantsSoldOut() {
      const variants = this.variants || [];
      if (!variants.length) return false;

      return variants.every((variant) => !this.isPurchasable(variant));
    },
    shouldShow() {
      return (
        this.expressCheckoutVersion !== 0 &&
        !!this.selectedVariant &&
        this.variantIsPurchasable &&
        !this.allVariantsSoldOut &&
        !this.isSelectedVariantPreorder &&
        !this.availableForPreOrder &&
        !this.availableForPrebuy &&
        !this.isPreLaunch &&
        !this.isGiftCard
      );
    },
  },
  watch: {
    shouldShow: {
      immediate: true,
      handler(show) {
        this.setVariantIdInput();
        if (show) {
          this.initPaymentButton();
        }
      },
    },
    selectedVariant: "setVariantIdInput",
  },
  mounted() {
    this.moveDynamicPaymentButton();
    this.setVariantIdInput();
    this.observeWalletAppearance();
    if (this.shouldShow) {
      this.initPaymentButton();
    }
  },
  beforeUnmount() {
    this._walletObserver?.disconnect();
    this._walletObserver = null;
    this.parkDynamicPaymentButton();
  },
  methods: {
    getPaymentButtonsEl() {
      return document.getElementById("dynamic-payment-buttons");
    },
    isPurchasable(variant) {
      if (!variant?.availableForSale) return false;

      // Some product queries omit quantityAvailable; fall back to availableForSale.
      if (
        variant.quantityAvailable == null ||
        Number.isNaN(variant.quantityAvailable)
      ) {
        return true;
      }

      return variant.quantityAvailable > 0;
    },
    moveDynamicPaymentButton() {
      const dynamicPaymentButtons = this.getPaymentButtonsEl();
      const target = this.$refs.dynamicPaymentButtonsTarget;
      if (!dynamicPaymentButtons || !target) return;

      // Remember where the Liquid block came from so it can be parked there
      // again on unmount instead of being destroyed with this component.
      this._sourceParent = dynamicPaymentButtons.parentElement;

      dynamicPaymentButtons.classList.remove("dynamic-payment-buttons--source");
      dynamicPaymentButtons.removeAttribute("aria-hidden");
      dynamicPaymentButtons.removeAttribute("inert");
      dynamicPaymentButtons.removeAttribute("style");
      target.append(dynamicPaymentButtons);
    },
    parkDynamicPaymentButton() {
      const dynamicPaymentButtons = this.getPaymentButtonsEl();
      if (!dynamicPaymentButtons) return;

      const input = dynamicPaymentButtons.querySelector(".product-variant-id");
      if (input) input.disabled = true;

      dynamicPaymentButtons.classList.add("dynamic-payment-buttons--source");
      dynamicPaymentButtons.setAttribute("aria-hidden", "true");
      dynamicPaymentButtons.setAttribute("inert", "");
      dynamicPaymentButtons.setAttribute("style", HIDDEN_STYLE);

      const parent = this._sourceParent?.isConnected
        ? this._sourceParent
        : document.body;
      parent.append(dynamicPaymentButtons);
    },
    setVariantIdInput() {
      try {
        const dynamicPaymentButtons = this.getPaymentButtonsEl();
        const input = dynamicPaymentButtons?.querySelector(
          ".product-variant-id"
        );
        if (!input) return;

        const variantId = this.selectedVariant?.id?.split(
          "gid://shopify/ProductVariant/"
        )[1];

        if (this.shouldShow && variantId) {
          input.disabled = false;
          input.value = variantId;
          input.dispatchEvent(new Event("change", { bubbles: true }));
        } else {
          input.disabled = true;
        }
      } catch (error) {
        console.error("Error setting variant ID input:", error);
      }
    },
    async initPaymentButton() {
      await this.$nextTick();
      if (window.Shopify?.PaymentButton?.init) {
        window.Shopify.PaymentButton.init();
      }
      this.applyWalletAppearance();
    },
    observeWalletAppearance() {
      const root = this.getPaymentButtonsEl();
      if (!root || this._walletObserver) return;

      this.applyWalletAppearance();
      this._walletObserver = new MutationObserver(() => {
        this.applyWalletAppearance();
      });
      this._walletObserver.observe(root, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ["recommended"],
      });
    },
    applyWalletAppearance() {
      const wallet = this.getPaymentButtonsEl()?.querySelector(
        "shopify-accelerated-checkout"
      );
      if (!wallet) {
        this.outlineWallet = false;
        return;
      }

      let name = "";
      try {
        const recommended = wallet.getAttribute("recommended");
        if (recommended) {
          name = JSON.parse(recommended)?.name || "";
        }
      } catch {
        name = "";
      }

      const outline = OUTLINE_WALLETS.has(name);
      this.outlineWallet = outline;
      wallet.classList.toggle("wallet--outline", outline);
    },
  },
};
</script>

<style lang="scss" scoped>
.dynamic-checkout-buttons {
  width: 100%;
  box-sizing: border-box;
  border-radius: 4px;
  overflow: hidden;
  background: $color-white;

  :deep(.shopify-payment-button),
  :deep(.shopify-payment-button__button),
  :deep(shopify-accelerated-checkout) {
    display: block;
    width: 100%;
    --shopify-accelerated-checkout-button-block-size: 44px;
    --shopify-accelerated-checkout-button-border-radius: 4px;
    --shopify-accelerated-checkout-button-box-shadow: none;
    --shopify-accelerated-checkout-skeleton-background-color: #{$color-white};
  }

  :deep(.shopify-payment-button__button) {
    min-height: 44px;
  }

  :deep(.shopify-payment-button__button--unbranded) {
    background-color: $color-black;
    color: $color-white;

    &:hover:not([disabled]) {
      background-color: $color-black;
    }
  }

  :deep(.shopify-payment-button__more-options) {
    display: none;
  }

  &--outline {
    border: 1px solid $color-black;

    :deep(shopify-accelerated-checkout.wallet--outline) {
      filter: invert(1);
    }
  }
}
</style>
